import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { saveOAuthToken } from '../firebase/oauthService';

const OAuthCallback = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { currentUser } = useAuth();
  const [status, setStatus] = useState('İşleniyor...');

  useEffect(() => {
    const handleCallback = async () => {
      const code = searchParams.get('code');
      const state = searchParams.get('state');
      const error = searchParams.get('error');
      const errorDescription = searchParams.get('error_description');

      if (error) {
        setStatus(`❌ Bağlantı reddedildi: ${errorDescription || error}`);
        setTimeout(() => navigate('/dashboard'), 3000);
        return;
      }

      if (!code || !state) {
        setStatus('❌ Geçersiz yanıt');
        setTimeout(() => navigate('/dashboard'), 2000);
        return;
      }

      // State'den platform ve userId'yi çıkar
      const [platform, userId] = state.split('_');

      if (userId !== currentUser?.uid) {
        setStatus('❌ Kullanıcı uyuşmazlığı');
        setTimeout(() => navigate('/dashboard'), 2000);
        return;
      }

      try {
        setStatus(`${platform.toUpperCase()} bağlanıyor...`);

        // Platform'a göre endpoint belirle
        let endpoint;
        let requestBody = {
          code,
          redirectUri: `${window.location.origin}/oauth/callback`
        };

        // Twitter için code verifier ekle (PKCE)
        if (platform === 'twitter') {
          const codeVerifier = localStorage.getItem('twitter_code_verifier');
          if (!codeVerifier) {
            throw new Error('Code verifier bulunamadı');
          }
          requestBody.codeVerifier = codeVerifier;
          localStorage.removeItem('twitter_code_verifier');
        }

        // Endpoint seç
        switch (platform) {
          case 'instagram':
            endpoint = `https://us-central1-socialblast-app.cloudfunctions.net/exchangeInstagramToken`;
            break;
          case 'facebook':
            endpoint = `https://us-central1-socialblast-app.cloudfunctions.net/exchangeFacebookToken`;
            break;
          case 'twitter':
            endpoint = `https://us-central1-socialblast-app.cloudfunctions.net/exchangeTwitterToken`;
            break;
          case 'linkedin':
            endpoint = `https://us-central1-socialblast-app.cloudfunctions.net/exchangeLinkedInToken`;
            break;
          default:
            throw new Error(`${platform} için OAuth henüz desteklenmiyor`);
        }

        // Backend'e code gönder, token al
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody)
        });

        const data = await response.json();

        if (data.success) {
          // Token'ı kaydet + ekstra bilgiler (userId, pageId, pageName, name)
          const additionalData = {};
          if (data.userId) additionalData.userId = data.userId;
          if (data.pageId) additionalData.pageId = data.pageId;
          if (data.pageName) additionalData.pageName = data.pageName;
          if (data.name) additionalData.name = data.name;

          await saveOAuthToken(
            userId,
            platform,
            data.token,
            data.username || data.userId,
            data.refreshToken,
            additionalData  // Ekstra bilgileri gönder
          );
          setStatus(`✅ ${platform.toUpperCase()} başarıyla bağlandı!`);

          // Popup'ta mıyız kontrol et
          const isPopup = window.opener && window.opener !== window;

          if (isPopup) {
            // Popup ise ana pencereyi yenile ve popup'ı kapat
            setTimeout(() => {
              if (window.opener) {
                window.opener.location.reload();
              }
              window.close();
            }, 1500);
          } else {
            // Normal sayfa ise dashboard'a yönlendir
            setTimeout(() => navigate('/dashboard'), 1500);
          }
        } else {
          setStatus(`❌ Hata: ${data.error || data.message}`);
          const isPopup = window.opener && window.opener !== window;

          // Hata bilgilerini localStorage'a kaydet
          if (data.error === 'NO_FACEBOOK_PAGE' || data.error === 'NO_INSTAGRAM_ON_PAGE') {
            localStorage.setItem('instagram_setup_error', JSON.stringify({
              errorType: data.error,
              message: data.message,
              pages: data.pages,
              setupUrl: data.setupUrl
            }));
          }

          setTimeout(() => {
            if (isPopup) {
              // Ana pencereyi yenile ki modal açılsın
              if (window.opener) {
                window.opener.location.reload();
              }
              window.close();
            } else {
              navigate('/dashboard');
            }
          }, 2000);
        }
      } catch (error) {
        console.error('OAuth callback error:', error);
        setStatus(`❌ Bağlantı hatası: ${error.message}`);
        setTimeout(() => navigate('/dashboard'), 3000);
      }
    };

    if (currentUser) {
      handleCallback();
    }
  }, [searchParams, currentUser, navigate]);

  // Popup'ta mı çalışıyoruz kontrol et
  const isPopup = window.opener && window.opener !== window;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50">
      <div className="bg-white p-8 rounded-2xl shadow-2xl text-center max-w-md">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mx-auto mb-4"></div>
        <h2 className="text-xl font-bold text-gray-800 mb-2">{status}</h2>
        <p className="text-gray-600 text-sm">
          {isPopup ? 'Bağlantı tamamlanıyor, popup kapanacak...' : 'Lütfen bekleyin, yönlendiriliyorsunuz...'}
        </p>
      </div>
    </div>
  );
};

export default OAuthCallback;